/**
 * Postcard design questionnaire — shared by the AI ($99) and custom ($300)
 * paths. Captures the key messaging that drives the AI art prompts and the
 * typography overlay (or briefs the human designer for custom orders), plus an
 * optional candidate headshot whose background is removed IN THE BROWSER
 * (@imgly/background-removal, lazy-loaded) so only a ready cut-out PNG ever
 * reaches the server.
 */
import React, { useState } from 'react';

const PRIMARY = '#112030';
const input = { width: '100%', padding: '10px 12px', border: '1px solid #dee2e6', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 12 };
const label = { fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 };
const chip = { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eef2f7', color: PRIMARY, borderRadius: 999, padding: '4px 12px', fontSize: 13, fontWeight: 600, marginRight: 6, marginBottom: 6 };

export const EMPTY_QUESTIONNAIRE = {
  keyMessage: '', topIssues: [], tone: 'hopeful and professional', colorPreference: '',
  callToAction: '', website: '', disclaimer: '', audienceNotes: '', extraNotes: '',
};

const TONES = ['hopeful and professional', 'bold and energetic', 'warm and neighborly', 'serious and trustworthy'];
const COLORS = [['', 'Match my party'], ['red', 'Red'], ['blue', 'Blue'], ['navy', 'Navy'], ['green', 'Green'], ['teal', 'Teal'], ['purple', 'Purple']];

// Downscale a blob to a ≤800px PNG data-less base64 string via canvas.
// Exported for reuse by the design editor (which passes a higher print cap).
export async function blobToPngB64(blob, maxPx = 800) {
  const bmp = await createImageBitmap(blob);
  const scale = Math.min(1, maxPx / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bmp.width * scale);
  canvas.height = Math.round(bmp.height * scale);
  canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/png').replace(/^data:image\/png;base64,/, '');
}

const HeadshotUploader = ({ headshotB64, onHeadshot }) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setProcessing(true); setError('');
    try {
      // Lazy-load the WASM background remover only when a photo is chosen.
      const { removeBackground } = await import('@imgly/background-removal');
      // Worker + timeout: keeps Firefox's main thread free (Brian's freeze).
      const cutout = await Promise.race([
        removeBackground(file, { proxyToWorker: true }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Background removal timed out')), 90000)),
      ]);
      onHeadshot(await blobToPngB64(cutout));
    } catch (err) {
      console.error('background removal failed:', err);
      setError('Could not process the photo — try a different image, or continue without one.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <label style={label}>Candidate photo (optional — background is removed automatically)</label>
      {headshotB64 ? (
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <div style={{
            width: 110, height: 110, borderRadius: 10, border: '1px solid #dee2e6', overflow: 'hidden',
            backgroundImage: 'linear-gradient(45deg,#eee 25%,transparent 25%),linear-gradient(-45deg,#eee 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#eee 75%),linear-gradient(-45deg,transparent 75%,#eee 75%)',
            backgroundSize: '16px 16px', backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
          }}>
            <img alt="Candidate cut-out preview" src={`data:image/png;base64,${headshotB64}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#166534', fontWeight: 600, marginBottom: 6 }}>✓ Background removed — this cut-out goes on the front.</div>
            <button type="button" onClick={() => onHeadshot(null)} style={{ background: 'none', border: '1px solid #dee2e6', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer', color: '#475569' }}>Remove photo</button>
          </div>
        </div>
      ) : (
        <div>
          <input type="file" accept="image/png,image/jpeg" onChange={onFile} disabled={processing} />
          {processing && <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>Removing background… (first time downloads the model, ~10–20s)</div>}
          {error && <div style={{ fontSize: 13, color: '#991b1b', marginTop: 6 }}>{error}</div>}
        </div>
      )}
    </div>
  );
};

const PostcardQuestionnaire = ({ values, onChange, headshotB64, onHeadshot }) => {
  const [issueDraft, setIssueDraft] = useState('');
  const set = (k, v) => onChange({ ...values, [k]: v });

  const addIssue = () => {
    const t = issueDraft.trim();
    if (!t || values.topIssues.length >= 3) return;
    set('topIssues', [...values.topIssues, t]);
    setIssueDraft('');
  };

  return (
    <div>
      <label style={label}>Key message *</label>
      <input style={input} value={values.keyMessage} onChange={e => set('keyMessage', e.target.value)}
        placeholder='The one thing voters should remember — e.g. "Lower taxes. Better rural roads."' />

      <label style={label}>Top issues (up to 3)</label>
      <div style={{ marginBottom: values.topIssues.length ? 6 : 0 }}>
        {values.topIssues.map((iss, i) => (
          <span key={i} style={chip}>{iss}
            <button type="button" onClick={() => set('topIssues', values.topIssues.filter((_, j) => j !== i))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, fontSize: 14 }}>×</button>
          </span>
        ))}
      </div>
      {values.topIssues.length < 3 && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...input, marginBottom: 12, flex: 1 }} value={issueDraft} onChange={e => setIssueDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addIssue(); } }} placeholder="e.g. public safety" />
          <button type="button" onClick={addIssue} style={{ height: 40, padding: '0 16px', border: `1px solid ${PRIMARY}`, background: 'transparent', color: PRIMARY, borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Add</button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <div>
          <label style={label}>Tone</label>
          <select style={{ ...input, cursor: 'pointer' }} value={values.tone} onChange={e => set('tone', e.target.value)}>
            {TONES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <label style={label}>Color direction</label>
          <select style={{ ...input, cursor: 'pointer' }} value={values.colorPreference} onChange={e => set('colorPreference', e.target.value)}>
            {COLORS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      <label style={label}>Call to action (printed on the back)</label>
      <input style={input} value={values.callToAction} onChange={e => set('callToAction', e.target.value)}
        placeholder="e.g. Vote early starting Oct 29 — I'm asking for your vote." />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <div>
          <label style={label}>Website (optional)</label>
          <input style={input} value={values.website} onChange={e => set('website', e.target.value)} placeholder="janesmith.com" />
        </div>
        <div>
          <label style={label}>Disclaimer (optional)</label>
          <input style={input} value={values.disclaimer} onChange={e => set('disclaimer', e.target.value)} placeholder='Default: "Paid for by Friends of …"' />
        </div>
      </div>

      <label style={label}>Who is this reaching? (optional)</label>
      <input style={input} value={values.audienceNotes} onChange={e => set('audienceNotes', e.target.value)} placeholder="e.g. likely primary voters in rural precincts" />

      <label style={label}>Anything else the designer should know? (optional)</label>
      <input style={input} value={values.extraNotes} onChange={e => set('extraNotes', e.target.value)} placeholder="e.g. avoid stock-photo look; our yard signs are navy" />

      <HeadshotUploader headshotB64={headshotB64} onHeadshot={onHeadshot} />
    </div>
  );
};

export default PostcardQuestionnaire;
